/* eslint-disable @typescript-eslint/no-explicit-any */
import { FormikHelpers } from 'formik';
import { AccountInfo, IPublicClientApplication } from '@azure/msal-browser';
import {
    FormStepStatusDto,
    OrganisationAndContact,
    RequestForQuoteClient,
    YesNo,
} from '../../api/web-api-client';
import { tokenRequest } from '../../authentication/authConfig';
import { ErrorType, WizardFormStepValues, WizardStepProps } from '../../components/forms/WizardForm/types';
import { discardChanges } from '../common/constants';
import { organisationAndContactSaveValidation, organisationAndContactSubmitValidation } from './validation';
import { AccountDetails } from '../../authentication/accountContext';
import { formatBannerTitle } from '../common/helperFunctions';
import AppLogger from '../../instrumentation/AppLogger';

const loadOrganisationAndContact = (id: string, accounts: AccountInfo[], instance: IPublicClientApplication) => async (
    abortSignal?: AbortSignal,
) => {
    if (accounts.length > 0) {
        try {
            const client = new RequestForQuoteClient();
            const tokenResult = await instance.acquireTokenSilent({
                ...tokenRequest,
                account: accounts[0],
            });
            client.setAuthToken(tokenResult.accessToken);
            const organisationAndContact = await client.getOrganisationAndContact(id, abortSignal);
            const wizardStepValues: WizardFormStepValues<OrganisationAndContact> = {
                stepValues: { ...organisationAndContact },
            };

            return wizardStepValues;
        } catch (error) {
            AppLogger.error('Failed to load organisation and contact details', error as Error, { Id: id });
            throw Error(`Failed to load organisation and contact details. Id: ${id}`);
        }
    } else {
        AppLogger.verbose('There are no accounts available to load organisation and contact details', { Id: id, Accounts: accounts });
        throw Error(`There are no accounts available to load organisation and contact details. Id: ${id}`);
    }
};

const saveStep = (
    id: string,
    accounts: AccountInfo[],
    instance: IPublicClientApplication,
    isComplete: boolean,
) => async (
    values: OrganisationAndContact,
    isDirty: boolean,
    _: FormikHelpers<OrganisationAndContact>,
    abortSignal?: AbortSignal,
) => {
    if (accounts.length > 0) {
        try {
            const client = new RequestForQuoteClient();
            const tokenResult = await instance.acquireTokenSilent({
                ...tokenRequest,
                account: accounts[0],
            });
            client.setAuthToken(tokenResult.accessToken);
            AppLogger.verbose(`Saving organisation and contact details. Id: ${id}`, { values, isComplete });
            await client.saveOrganisationAndContact(
                id,
                {
                    applicationId: id,
                    formStep: values,
                    isCompletingStep: isComplete,
                },
                abortSignal,
            );
        } catch (error) {
            AppLogger.error('Failed to save organisation and contact', error as Error, { Id: id });
            throw Error(`Failed to save organisation and contact details. Id: ${id}`);
        }
    } else {
        AppLogger.verbose('There are no accounts available to save organisation and contact details', { Id: id, Accounts: accounts });
        throw Error(`There are no accounts available to save organisation and contact details. Id: ${id}`);
    }
};

const getRedirectionLocationOnError = (id: string) => (errorCode: number, errorType: ErrorType) => '/not-found';

const organisationAndContactProps = (
    id: string,
    accounts: AccountInfo[],
    instance: IPublicClientApplication,
    accountDetails: AccountDetails,
    statuses: FormStepStatusDto[],
    bannerTitle: string,
)
: WizardStepProps<OrganisationAndContact> => ({
    initialValues: {
    },
    stepStatuses: statuses,
    loadStepValues: loadOrganisationAndContact(id, accounts, instance),
    location: '/organisation-and-contact',
    title: 'Organisation and contact',
    hidingFields: {
        businessOrTradingName: (x) => false,
        branchOrLocationName: (x) => false,
        principalContactHide: (x) => x.isPrincipalContact === YesNo.Yes,
        contact: {
            titleOther: (x) => x.contact?.title !== 'Other',
        },
    },
    validateHard: organisationAndContactSubmitValidation,
    validateSoft: organisationAndContactSaveValidation,
    onSaveAndExit: saveStep(id, accounts, instance, false),
    onSaveAndNext: saveStep(id, accounts, instance, true),
    bannerTitle,
    bannerRefTitle: `Ref ID: ${id}`,
    bannerSubTitle: formatBannerTitle(accountDetails),
    getRedirectionLocationOnError: getRedirectionLocationOnError(id),
    discard: discardChanges,
});

export default organisationAndContactProps;
